import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { DeleteBundle } from './delete-bundle'
import { bundlesService } from '@/api/bundles/bundles-service'
import type { Bundle } from '@/api/bundles/bundles-types'
import { NumberInput } from '@/components/ui/number-input'
import { cn } from '@/lib/utils'

interface UpdateBundleProps {
  bundle: Bundle
  readOnly?: boolean
  /** Narrow control for dense tables (e.g. detail sidebar). */
  compact?: boolean
  className?: string
}

export const UpdateBundle = ({ bundle, readOnly = false, compact = false, className }: UpdateBundleProps) => {
  const [quantity, setQuantity] = useState<number>(bundle.quantity ?? 0)

  const [openDelete, setOpenDelete] = useState(false)

  const queryClient = useQueryClient()

  const updateBundleMutation = useMutation({
    mutationFn: ({ quantity }: { quantity: number }) =>
      bundlesService.update(bundle.id, { quantity, origin_item: bundle.origin_item }),
    onSuccess: () => {
      toast.success('Bundle edited successfully')

      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['timeline-orders'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
    },
    onError: () => {
      setQuantity(bundle.quantity ?? 0)
    }
  })

  const handleQuantity = (newQuantity: number) => {
    if (newQuantity <= 0) {
      setOpenDelete(true)
      return
    }

    const quantityToSet = newQuantity > 10 ? 10 : newQuantity
    setQuantity(quantityToSet)

    if (newQuantity <= 10 && newQuantity > 0) {
      updateBundleMutation.mutate({ quantity: quantityToSet })
    }
  }

  if (readOnly) {
    return quantity ?? '-'
  }

  return (
    <>
      <DeleteBundle
        bundle={bundle}
        open={openDelete}
        onOpenChange={setOpenDelete}
      >
        <div />
      </DeleteBundle>
      <NumberInput
        className={cn(
          compact ? 'min-w-30 max-w-30' : 'max-w-40',
          'data-focus-within:ring-transparent',
          className
        )}
        onChange={handleQuantity}
        value={quantity}
        minValue={0}
      />
    </>
  )
}
